// LivePlayPage — matchmaking with AI seat-holding
// Completely isolated from existing community rooms.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getSocket, connectSocket } from '../socketClient';

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 700);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 700);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

const MODES = [
  {
    id: 'buck',
    label: 'Buck Euchre',
    desc: 'Wager Bucks · 3 or 4 players',
    color: '#16a34a',
    bg: 'linear-gradient(135deg, #052e16, #14532d)',
    border: '#16a34a',
  },
  {
    id: 'euchre',
    label: 'Euchre',
    desc: 'Classic partner Euchre · 4 players',
    color: '#0ea5e9',
    bg: 'linear-gradient(135deg, #0c1a2e, #0c3358)',
    border: '#0ea5e9',
  },
];

export default function LivePlayPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [status, setStatus] = useState('select'); // 'select' | 'finding' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const handleJoin = async (gameMode) => {
    setStatus('finding');
    setErrorMsg('');
    try {
      const res = await fetch('/api/liveplay/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ gameMode }),
      });
      const data = await res.json();
      if (!data.success) { setErrorMsg(data.error || 'Could not find a game.'); setStatus('error'); return; }

      // Connect socket if needed
      if (!getSocket()?.connected) connectSocket(token);

      // Pre-seed seated param so playerLabels shows correct names immediately
      const seatedInfo = data.isHost
        ? JSON.stringify([{ userId: user?.userId, nickname: user?.nickname || 'You', seatIndex: 0 }])
        : JSON.stringify([{ userId: user?.userId, nickname: user?.nickname || 'You', seatIndex: data.seatIndex }]);
      const params = new URLSearchParams({
        community: data.roomCode,
        host: data.isHost ? 'true' : 'false',
        seat: String(data.seatIndex),
        players: '4',
        liveplay: 'true',
        autostart: 'true',  // skip wager screen, start immediately in free play
        seated: seatedInfo,
      });
      const path = gameMode === 'euchre' ? '/euchre' : '/play';
      navigate(`${path}?${params.toString()}`);
    } catch (e) {
      setErrorMsg('Connection error. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at top, #1a1008 0%, #0d0a05 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: isMobile ? '32px 16px' : '60px 24px',
      fontFamily: "'Georgia', serif", color: '#e2e8f0',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: isMobile ? 32 : 48 }}>
        <div style={{ fontSize: isMobile ? 11 : 12, letterSpacing: 3, textTransform: 'uppercase', color: '#d4af37', marginBottom: 8 }}>
          New Feature
        </div>
        <h1 style={{ fontSize: isMobile ? 28 : 42, fontWeight: 700, margin: 0, color: '#fff' }}>
          Live Play
        </h1>
        <p style={{ color: '#94a3b8', fontSize: isMobile ? 13 : 15, marginTop: 10, maxWidth: 420, lineHeight: 1.6 }}>
          Jump into a table instantly. AI players hold seats until real people join.
          When another human arrives, they take an AI seat and play continues.
        </p>
      </div>

      {status === 'select' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 20, width: '100%', maxWidth: 560,
        }}>
          {MODES.map(mode => (
            <button
              key={mode.id}
              onClick={() => handleJoin(mode.id)}
              style={{
                background: mode.bg,
                border: `2px solid ${mode.border}`,
                borderRadius: 16,
                padding: isMobile ? '28px 20px' : '36px 28px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'transform 0.15s, box-shadow 0.15s',
                boxShadow: `0 4px 20px rgba(0,0,0,0.4)`,
              }}
              onMouseEnter={e => { if (!isMobile) { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 8px 30px rgba(0,0,0,0.5), 0 0 20px ${mode.border}40`; } }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.4)'; }}
            >
              <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, color: mode.color, marginBottom: 6 }}>
                {mode.label}
              </div>
              <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>{mode.desc}</div>
              <div style={{
                display: 'inline-block',
                background: mode.color,
                color: '#000',
                fontWeight: 700,
                fontSize: 13,
                borderRadius: 8,
                padding: '8px 20px',
              }}>
                Find a Table →
              </div>
            </button>
          ))}
        </div>
      )}

      {status === 'finding' && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            border: '4px solid #30363d', borderTopColor: '#d4af37',
            animation: 'spin 0.9s linear infinite',
            margin: '0 auto 24px',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#e2e8f0' }}>Finding a table…</div>
          <div style={{ fontSize: 13, color: '#8b949e', marginTop: 8 }}>
            You'll be placed with AI players immediately while we fill the seats.
          </div>
        </div>
      )}

      {status === 'error' && (
        <div style={{ textAlign: 'center', maxWidth: 380 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 16, color: '#f87171', fontWeight: 600, marginBottom: 8 }}>{errorMsg}</div>
          <button
            onClick={() => setStatus('select')}
            style={{ background: '#21262d', border: '1px solid #30363d', color: '#c9d1d9', borderRadius: 8, padding: '10px 24px', cursor: 'pointer', fontSize: 14 }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* How it works */}
      {status === 'select' && (
        <div style={{ marginTop: 40, maxWidth: 500, width: '100%' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#484f58', marginBottom: 14, textAlign: 'center' }}>
            How it works
          </div>
          {[
            ['🤖', 'AI fills empty seats instantly so you\'re never waiting.'],
            ['👤', 'When another player joins, they take an AI seat and play continues.'],
            ['🃏', 'Seat swaps happen between hands — no mid-trick interruptions.'],
            ['🏠', 'First human in is the host. If host leaves, the table closes.'],
          ].map(([icon, text]) => (
            <div key={text} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
              <span style={{ fontSize: 13, color: '#8b949e', lineHeight: 1.5 }}>{text}</span>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => navigate('/home')}
        style={{ marginTop: 32, background: 'none', border: 'none', color: '#484f58', cursor: 'pointer', fontSize: 13 }}
      >
        ← Back to Home
      </button>
    </div>
  );
}
